import React, { useEffect, useState } from 'react';
import { completeProfile } from '../api/profile';
import { fetchCurrentCalculatorTermsVersion } from '../api/legalDocuments';
import { PROFILE_PARTICIPANT_ROLES } from '../data/profileRoles';
import { PROFILE_COUNTRIES } from '../data/countries';
import { productName, logoSrc, logoStyleCompact, calculatorTermsPath, privacyUrl } from '../brand';
import PageFooter from '../components/chrome/PageFooter';

export default function ProfileCompletion({ user, onCompleted, onSignOut, onOpenTerms }) {
  const [organization, setOrganization] = useState('');
  const [role, setRole] = useState('');
  const [country, setCountry] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [termsDocVersion, setTermsDocVersion] = useState('');
  const [termsVersionReady, setTermsVersionReady] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetchCurrentCalculatorTermsVersion()
      .then((v) => {
        if (!cancelled && v) {
          setTermsDocVersion(v);
          setTermsVersionReady(true);
        }
      })
      .catch(() => {
        if (!cancelled) setError('terms_unavailable');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const orgDisplay = organization.trim() || 'your organization';

  async function handleSubmit(event) {
    event.preventDefault();
    setError(null);
    if (!organization.trim() || !role || !country.trim()) {
      setError('Organization, role, and country are required.');
      return;
    }
    if (!termsDocVersion) {
      setError('Terms are not available yet. Please try again in a moment.');
      return;
    }
    if (!termsAccepted) {
      setError('Please accept the Terms of Use and Privacy Notice to continue.');
      return;
    }
    setSubmitting(true);
    try {
      const profile = await completeProfile({
        organization: organization.trim(),
        role: role,
        country: country.trim(),
        termsAccepted: true,
        termsDocVersion: termsDocVersion,
        marketingConsentFirstparty: marketingConsent,
      });
      onCompleted(profile);
    } catch (err) {
      setError(err?.code || err?.message || 'profile_update_failed');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={pageStyle}>
      <div style={contentStyle}>
        <div style={cardStyle}>
        <div style={cardHeaderStyle}>
          <img src={logoSrc} alt={productName} style={logoStyleCompact} />
          {onSignOut && (
            <button type="button" onClick={onSignOut} style={signOutStyle}>
              Sign out
            </button>
          )}
        </div>
        <h1 style={titleStyle}>Complete your profile</h1>
        <p style={ledeStyle}>
          {user?.name || user?.email
            ? `Welcome${user.name ? `, ${user.name}` : ''}. `
            : ''}
          A few details before you can save valuations.
        </p>

        <form onSubmit={handleSubmit} style={formStyle}>
          <label style={labelStyle}>
            Organization
            <input
              type="text"
              value={organization}
              onChange={(e) => setOrganization(e.target.value)}
              style={inputStyle}
              autoComplete="organization"
              required
            />
          </label>
          <label style={labelStyle}>
            Country
            <select
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              style={selectStyle}
              required
              data-testid="profile-country-select"
            >
              <option value="">Select country</option>
              {PROFILE_COUNTRIES.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </label>
          <label style={labelStyle}>
            How do you participate?
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              style={selectStyle}
              required
              data-testid="profile-role-select"
            >
              <option value="">Select seller, buyer, or both</option>
              {PROFILE_PARTICIPANT_ROLES.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label style={checkRowStyle} data-testid="profile-terms-row">
            <input
              type="checkbox"
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.target.checked)}
              data-testid="profile-terms-accept"
            />
            <span>
              I agree to the{' '}
              <a
                href={calculatorTermsPath}
                style={linkStyle}
                onClick={
                  onOpenTerms
                    ? (e) => {
                        e.preventDefault();
                        onOpenTerms();
                      }
                    : undefined
                }
              >
                Terms of Use
              </a>
              {' '}and{' '}
              <a href={privacyUrl} target="_blank" rel="noopener noreferrer" style={linkStyle}>
                Platform Privacy Notice
              </a>
              , and I confirm I am at least 18 years old and authorized to act on behalf of{' '}
              <strong>{orgDisplay}</strong> in using this Platform.
            </span>
          </label>
          <label style={{ ...checkRowStyle, ...marketingRowStyle }} data-testid="profile-marketing-row">
            <input
              type="checkbox"
              checked={marketingConsent}
              onChange={(e) => setMarketingConsent(e.target.checked)}
              data-testid="profile-marketing-opt-in"
            />
            <span>
              Send me occasional updates about new LooporaData features, the Marketplace, and product
              offerings. You can unsubscribe anytime.
            </span>
          </label>
          {error && <p style={errorStyle}>{error}</p>}
          <button type="submit" style={submitStyle} disabled={submitting || !termsVersionReady}>
            {submitting ? 'Saving…' : 'Continue to My valuations'}
          </button>
        </form>
        </div>
      </div>
      <PageFooter />
    </div>
  );
}

const pageStyle = {
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  background: 'var(--bg)',
  fontFamily: 'var(--font)',
};

const contentStyle = {
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '32px 24px',
};

const cardStyle = {
  width: '100%',
  maxWidth: '480px',
  padding: '36px 40px',
  background: 'var(--surface)',
  border: '1px solid var(--line)',
  borderRadius: '8px',
};

const titleStyle = {
  fontFamily: 'var(--display)',
  fontSize: '28px',
  fontWeight: 500,
  margin: '20px 0 0',
  color: 'var(--ink)',
};

const ledeStyle = { fontSize: '14px', color: 'var(--ink2)', lineHeight: 1.55, margin: '10px 0 0' };
const formStyle = { display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '24px' };
const labelStyle = { display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '13px', color: 'var(--ink2)' };
const inputStyle = {
  height: '44px',
  padding: '0 12px',
  border: '1px solid var(--line)',
  borderRadius: '4px',
  fontSize: '14px',
  fontFamily: 'var(--font)',
  color: 'var(--ink)',
  background: 'var(--surface)',
};
const selectStyle = {
  ...inputStyle,
  width: '100%',
  cursor: 'pointer',
};
const checkRowStyle = { display: 'flex', alignItems: 'flex-start', gap: '10px', fontSize: '13px', color: 'var(--ink2)' };
const marketingRowStyle = {
  marginTop: '4px',
  paddingTop: '12px',
  borderTop: '1px solid var(--line)',
};
const linkStyle = { color: 'var(--accent)' };
const errorStyle = { margin: 0, fontSize: '13px', color: 'var(--error)' };
const cardHeaderStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};

const signOutStyle = {
  background: 'transparent',
  border: 'none',
  color: 'var(--ink2)',
  cursor: 'pointer',
  font: 'inherit',
  fontSize: '13px',
  padding: '4px 0',
};

const submitStyle = {
  height: '48px',
  marginTop: '8px',
  border: 'none',
  borderRadius: '999px',
  background: 'var(--accent)',
  color: '#fff',
  fontSize: '14px',
  fontWeight: 500,
  cursor: 'pointer',
  fontFamily: 'var(--font)',
};
